import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Button,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import ConfirmationModal from "../../components/confirmation-modal";
import FileDrawer from "../../components/file-drawer";
import FileUploadModal from "../../components/file-upload-modal";
import Header from "../../components/header";
import ReportChartDetail from "../../components/report-chart-detail";
import SectionTable from "../../components/section-table";
import axios from "../../config/axios";
import { BISAC_FIELDS, getSections, getStaticColumns, KEYWORD_FIELDS } from "./ebook-table-schema";
import { TBisacEntry, TEBook, TPage } from "./types";
import {
  TModalType,
  TBulkBisacModalState, TBulkKeywordModalState, TBisacFieldEditState, TPendingBisacFieldEdit,
} from "./ebooks-page.config";

const FILE_LIST = "FILE_LIST";
const FILE_ADD = "FILE_ADD";

const EBooksPage = (): JSX.Element => {
  const { t } = useTranslation();
  let [searchParams, setSearchParams] = useSearchParams();
  const [selectedEbook, setSelectedEbook] = useState<string>();
  const [modal, setModal] = useState<TModalType | undefined>(undefined);
  const [bulkKeywordModal, setBulkKeywordModal] = useState<TBulkKeywordModalState | undefined>(undefined);
  const [bulkBisacModal, setBulkBisacModal] = useState<TBulkBisacModalState | undefined>(undefined);
  const [pendingKeywordEdits, setPendingKeywordEdits] = useState<Map<string, Record<string, string>>>(new Map());
  const [pendingBisacEdits, setPendingBisacEdits] = useState<Map<string, Record<string, TPendingBisacFieldEdit>>>(new Map());
  const [savingAll, setSavingAll] = useState(false);
  const queryClient = useQueryClient();
  const activeSection = searchParams.get("section") || "links";
  const hiddenColumns = new Set((searchParams.get("hidden") || "").split(",").filter(Boolean));

  const handleToggleColumn = (key: string): void => {
    const params = new URLSearchParams(searchParams);
    const next = new Set(hiddenColumns);
    next.has(key) ? next.delete(key) : next.add(key);
    const joined = Array.from(next).join(",");
    joined ? params.set("hidden", joined) : params.delete("hidden");
    setSearchParams(params);
  };
  const { data, error, fetchNextPage, hasNextPage, status } = useInfiniteQuery<
    TPage<TEBook>
  >(
    ["users", searchParams.get("fieldName"), searchParams.get("direction")],
    async ({ pageParam = 0 }) => {
      const response = await axios
        .get(`/api/ebook?${searchParams}&page=${pageParam}`)
        .then((res) => res.data);
      const data: TPage<TEBook> = {
        results: response,
        next: response?.length > 0 ? pageParam + 1 : undefined,
      };
      return data;
    },
    {
      getNextPageParam: (lastPage) => lastPage.next,
      refetchOnWindowFocus: false,
    }
  );

  const handleSort = (fieldName: string): void => {
    let direction = "DESC";
    if (
      fieldName === searchParams.get("fieldName") &&
      searchParams.get("direction") === "DESC"
    ) {
      direction = "ASC";
    }
    const params = new URLSearchParams(searchParams);
    params.set("fieldName", fieldName);
    params.set("direction", direction);
    setSearchParams(params);
  };

  const handleSectionChange = (sectionIdentifier: string): void => {
    const params = new URLSearchParams(searchParams);
    params.set("section", sectionIdentifier);
    setSearchParams(params);
  };

  const updateBulkBisacField = (fieldName: string, updates: Partial<TBisacFieldEditState>): void => {
    setBulkBisacModal((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        fields: {
          ...prev.fields,
          [fieldName]: { ...prev.fields[fieldName], ...updates },
        },
      };
    });
  };

  const openBulkBisacModal = (ebook: TEBook): void => {
    const pending = pendingBisacEdits.get(ebook.isbn);
    const fields: Record<string, TBisacFieldEditState> = {};
    BISAC_FIELDS.forEach(({ field }) => {
      const entry = ebook.bisacs?.[field] as TBisacEntry;
      const p = pending?.[field];
      fields[field] = {
        code: p?.code ?? entry?.code ?? "",
        heading: p?.heading ?? entry?.heading ?? "",
        subHeading: p?.subHeading ?? entry?.subHeading ?? "",
        subHeadings: [],
        loadingSubHeadings: false,
        loadingCode: false,
      };
    });
    setBulkBisacModal({ ebook, headings: [], loadingHeadings: true, fields });
    axios.get<string[]>("/api/bisacs/headings").then((res) => {
      setBulkBisacModal((prev) =>
        prev ? { ...prev, headings: res.data.filter(Boolean), loadingHeadings: false } : prev
      );
    });
    BISAC_FIELDS.forEach(({ field }) => {
      if (fields[field].heading) {
        axios.post<string[]>("/api/bisacs/sub-headings", { heading: fields[field].heading }).then((res) => {
          setBulkBisacModal((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              fields: {
                ...prev.fields,
                [field]: { ...prev.fields[field], subHeadings: res.data.filter(Boolean) },
              },
            };
          });
        });
      }
    });
  };

  const handleBulkHeadingChange = (fieldName: string, value: string): void => {
    if (!value) {
      updateBulkBisacField(fieldName, {
        heading: "", subHeading: "", code: "",
        subHeadings: [], loadingSubHeadings: false, loadingCode: false,
      });
      return;
    }
    updateBulkBisacField(fieldName, {
      heading: value, subHeading: "", code: "",
      subHeadings: [], loadingSubHeadings: true, loadingCode: true,
    });
    axios.post<string[]>("/api/bisacs/sub-headings", { heading: value }).then((res) => {
      updateBulkBisacField(fieldName, { subHeadings: res.data.filter(Boolean), loadingSubHeadings: false });
    });
    axios
      .post<{ code: string; heading: string; subHeading: string }>("/api/bisacs/headings", { heading: value, subHeading: "" })
      .then((res) => {
        updateBulkBisacField(fieldName, { code: res.data.code, loadingCode: false });
      });
  };

  const handleBulkSubHeadingChange = (fieldName: string, value: string): void => {
    const heading = bulkBisacModal?.fields[fieldName]?.heading;
    if (heading && value) {
      updateBulkBisacField(fieldName, { subHeading: value, code: "", loadingCode: true });
      axios
        .post<{ code: string; heading: string; subHeading: string }>("/api/bisacs/headings", { heading, subHeading: value })
        .then((res) => {
          updateBulkBisacField(fieldName, { code: res.data.code, loadingCode: false });
        });
    } else {
      updateBulkBisacField(fieldName, { subHeading: value, code: "" });
    }
  };

  const handleBulkCodeChange = (fieldName: string, value: string): void => {
    updateBulkBisacField(fieldName, { code: value });
    if (value.length === 9) {
      axios
        .get<{ code: string; heading: string; subHeading: string } | null>(`/api/bisacs/${value}`)
        .then((res) => {
          if (res.data) {
            updateBulkBisacField(fieldName, {
              heading: res.data.heading,
              subHeading: res.data.subHeading,
              code: res.data.code,
              loadingSubHeadings: true,
            });
            axios.post<string[]>("/api/bisacs/sub-headings", { heading: res.data.heading }).then((subRes) => {
              updateBulkBisacField(fieldName, { subHeadings: subRes.data.filter(Boolean), loadingSubHeadings: false });
            });
          } else {
            updateBulkBisacField(fieldName, { heading: "", subHeading: "", subHeadings: [] });
          }
        });
    }
  };

  const handleBulkKeywordConfirm = (): void => {
    if (!bulkKeywordModal) return;
    setPendingKeywordEdits((prev) => {
      const next = new Map(prev);
      next.set(bulkKeywordModal.ebook.isbn, { ...bulkKeywordModal.values });
      return next;
    });
    setBulkKeywordModal(undefined);
  };

  const handleBulkBisacConfirm = (): void => {
    if (!bulkBisacModal) return;
    const edits: Record<string, TPendingBisacFieldEdit> = {};
    BISAC_FIELDS.forEach(({ field }) => {
      const f = bulkBisacModal.fields[field];
      edits[field] = { code: f.code, heading: f.heading, subHeading: f.subHeading };
    });
    setPendingBisacEdits((prev) => {
      const next = new Map(prev);
      next.set(bulkBisacModal.ebook.isbn, edits);
      return next;
    });
    setBulkBisacModal(undefined);
  };

  const handleSaveAll = async (): Promise<void> => {
    setSavingAll(true);
    try {
      if (activeSection === "keywords") {
        const payload = Array.from(pendingKeywordEdits.entries()).map(([isbn, keywords]) => {
          const original = rawData.find((e) => e.isbn === isbn);
          const prefixed: Record<string, string> = {};
          Object.entries(keywords).forEach(([field, value]) => {
            const orig = original?.amazonKeywords?.[field as keyof TEBook["amazonKeywords"]] || "";
            prefixed[field] = value !== orig ? `$_${value}` : value;
          });
          return { isbn, ...prefixed };
        });
        await axios.put("/api/ebook/keywords", payload);
      } else if (activeSection === "bisacs") {
        const payload = Array.from(pendingBisacEdits.entries()).map(([isbn, edits]) => {
          const original = rawData.find((e) => e.isbn === isbn);
          const prefixCode = (field: string): string | null => {
            const code = edits[field]?.code || null;
            if (!code) return null;
            const orig = (original?.bisacs?.[field as keyof TEBook["bisacs"]])?.code ?? "";
            return code !== orig ? `$_${code}` : code;
          };
          return {
            isbn,
            ebookBisacMain: prefixCode("ebookBisacMain"),
            ebookBisacSubOne: prefixCode("ebookBisacSubOne"),
            ebookBisacSubTwo: prefixCode("ebookBisacSubTwo"),
            ebookBisacSubThree: prefixCode("ebookBisacSubTree"),
            ebookBisacSubFour: prefixCode("ebookBisacSubFour"),
            ebookBisacSubFive: prefixCode("ebookBisacSubFive"),
          };
        });
        await axios.put("/api/ebook/bisacs", payload);
      }
      await queryClient.invalidateQueries(["users"]);
      setPendingKeywordEdits(new Map());
      setPendingBisacEdits(new Map());
    } catch (err) {
      console.error("Failed to save all:", err);
    }
    setSavingAll(false);
  };

  const handleEditRow = (ebook: TEBook): void => {
    if (activeSection === "keywords") {
      const pending = pendingKeywordEdits.get(ebook.isbn);
      const values: Record<string, string> = {};
      KEYWORD_FIELDS.forEach(({ field }) => {
        values[field] = pending?.[field] ?? (ebook.amazonKeywords?.[field as keyof TEBook["amazonKeywords"]] || "");
      });
      setBulkKeywordModal({ ebook, values });
    } else if (activeSection === "bisacs") {
      openBulkBisacModal(ebook);
    }
  };

  const isBulkBisacSaveDisabled = bulkBisacModal
    ? Object.values(bulkBisacModal.fields).some((f) => {
        if (f.loadingCode || f.loadingSubHeadings) return true;
        const allEmpty = !f.heading && !f.subHeading && !f.code;
        const allFilled = !!f.heading && !!f.subHeading && !!f.code;
        return !allEmpty && !allFilled;
      })
    : true;

  const rawData = data?.pages.flatMap((page) => page?.results ?? []) ?? [];

  const editedRows = activeSection === "keywords"
    ? new Set(pendingKeywordEdits.keys())
    : activeSection === "bisacs"
      ? new Set(pendingBisacEdits.keys())
      : undefined;

  const bisacFieldToId = Object.fromEntries(BISAC_FIELDS.map(({ id, field }) => [field, id]));

  const editedCells = (() => {
    const map = new Map<string, Set<string>>();
    if (activeSection === "keywords") {
      pendingKeywordEdits.forEach((edits, isbn) => {
        const original = rawData.find((e) => e.isbn === isbn);
        const changed = new Set<string>();
        Object.entries(edits).forEach(([field, value]) => {
          const orig = original?.amazonKeywords?.[field as keyof TEBook["amazonKeywords"]] || "";
          if (value !== orig) changed.add(field);
        });
        if (changed.size > 0) map.set(isbn, changed);
      });
    } else if (activeSection === "bisacs") {
      pendingBisacEdits.forEach((edits, isbn) => {
        const original = rawData.find((e) => e.isbn === isbn);
        const changed = new Set<string>();
        Object.entries(edits).forEach(([field, edit]) => {
          const orig = original?.bisacs?.[field as keyof TEBook["bisacs"]];
          if (edit.code !== (orig?.code ?? "")) {
            const colId = bisacFieldToId[field];
            if (colId) changed.add(colId);
          }
        });
        if (changed.size > 0) map.set(isbn, changed);
      });
    }
    return map.size > 0 ? map : undefined;
  })();

  const pendingCount = activeSection === "keywords"
    ? pendingKeywordEdits.size
    : activeSection === "bisacs"
      ? pendingBisacEdits.size
      : 0;
  const allData = rawData.map((ebook) => {
    const kwEdit = pendingKeywordEdits.get(ebook.isbn);
    const bisacEdit = pendingBisacEdits.get(ebook.isbn);
    if (!kwEdit && !bisacEdit) return ebook;
    let merged = ebook;
    if (kwEdit) {
      merged = { ...merged, amazonKeywords: { ...merged.amazonKeywords, ...kwEdit } };
    }
    if (bisacEdit) {
      const bisacs = { ...merged.bisacs };
      Object.entries(bisacEdit).forEach(([field, edit]) => {
        bisacs[field as keyof TEBook["bisacs"]] = { code: edit.code, heading: edit.heading, subHeading: edit.subHeading };
      });
      merged = { ...merged, bisacs };
    }
    return merged;
  });
  const dataLength = allData.length;

  const handleCoverClick = (ebook: TEBook): void => {
    if (ebook.cover) {
      const link = document.createElement("a");
      link.href = `/api/ebook/${ebook.isbn}?type=COVER`;
      link.download = `cover_${ebook.isbn}`;
      link.click();
    }
  };
  const staticColumns = getStaticColumns(
    t,
    (ebook: TEBook) => setSelectedEbook(ebook.isbn),
    handleCoverClick,
  );
  const handleFilesClick = (ebook: TEBook): void => {
    setModal({ isbn: ebook.isbn, type: FILE_LIST });
  };
  const sections = getSections(t, undefined, undefined, handleFilesClick);

  return (
    <>
      <div
        style={{ height: "100vh", overflow: "auto" }}
        id="scrollbar-target"
      >
        <div style={{ minWidth: "fit-content" }}>
          <Header />
          <InfiniteScroll
            dataLength={dataLength || 0}
            next={fetchNextPage}
            hasMore={!!hasNextPage}
            loader={
              <div
                style={{
                  color: "deepskyblue",
                  fontSize: "20px",
                  textAlign: "center",
                }}
              >
                <Spinner />
              </div>
            }
            data-testid="infinite-scroll"
            scrollableTarget="scrollbar-target"
            style={{ padding: "5px", overflow: "unset" }}
            endMessage={<></>}
          >
            <SectionTable<TEBook>
              staticColumns={staticColumns}
              sections={sections}
              data={allData}
              isLoading={status === "loading"}
              error={error}
              sort={{
                fieldName: searchParams.get("fieldName"),
                direction: searchParams.get("direction"),
              }}
              handleSort={handleSort}
              rowKey="isbn"
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              hiddenColumns={hiddenColumns}
              onToggleColumn={handleToggleColumn}
              editedRows={editedRows}
              editedCells={editedCells}
              onEditRow={(activeSection === "keywords" || activeSection === "bisacs") ? handleEditRow : undefined}
              actions={(activeSection === "keywords" || activeSection === "bisacs") ? (
                <Button
                  colorScheme="teal"
                  size="sm"
                  onClick={handleSaveAll}
                  isLoading={savingAll}
                  isDisabled={pendingCount === 0}
                >
                  {t("saveAllChanges")} ({pendingCount})
                </Button>
              ) : undefined}
            />
          </InfiniteScroll>
        </div>
      </div>
      <Modal
        isOpen={!!selectedEbook}
        onClose={() => setSelectedEbook(undefined)}
        size="full"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("report")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ReportChartDetail isbn={selectedEbook || ""} />
          </ModalBody>
        </ModalContent>
      </Modal>
      <FileDrawer
        isbn={modal?.isbn}
        isOpen={modal?.type === FILE_LIST}
        close={() => setModal(undefined)}
        addFile={() => setModal({ isbn: modal?.isbn || "", type: FILE_ADD })}
      />
      <FileUploadModal
        isbn={modal?.isbn}
        isOpen={modal?.type === FILE_ADD}
        close={() => setModal({ isbn: modal?.isbn || "", type: FILE_LIST })}
      />
      <ConfirmationModal
        isOpen={!!bulkKeywordModal}
        onClose={() => setBulkKeywordModal(undefined)}
        onConfirm={handleBulkKeywordConfirm}
        title={t("keywords")}
        confirmText={t("save")}
        draggable
        size="lg"
      >
        {bulkKeywordModal && (
          <VStack spacing={4} align="stretch">
            <Box pb={2} borderBottomWidth="1px" borderColor="gray.200">
              <Text fontWeight="bold" fontSize="sm" color="gray.600">{bulkKeywordModal.ebook.title}</Text>
              <Text fontSize="xs" color="gray.400">{bulkKeywordModal.ebook.isbn}</Text>
            </Box>
            {KEYWORD_FIELDS.map(({ field }) => (
              <FormControl key={field}>
                <FormLabel fontSize="sm">{`${t("keyword")} ${field.replace("keyword", "")}`}</FormLabel>
                <Input
                  value={bulkKeywordModal.values[field] || ""}
                  onChange={(e) => setBulkKeywordModal((prev) => prev ? { ...prev, values: { ...prev.values, [field]: e.target.value } } : prev)}
                />
              </FormControl>
            ))}
          </VStack>
        )}
      </ConfirmationModal>
      <ConfirmationModal
        isOpen={!!bulkBisacModal}
        onClose={() => setBulkBisacModal(undefined)}
        onConfirm={handleBulkBisacConfirm}
        title={t("bisacs")}
        confirmText={t("save")}
        confirmDisabled={isBulkBisacSaveDisabled}
        draggable
        size="xl"
      >
        {bulkBisacModal && (
          <VStack spacing={4} align="stretch">
            <Box pb={2} borderBottomWidth="1px" borderColor="gray.200">
              <Text fontWeight="bold" fontSize="sm" color="gray.600">{bulkBisacModal.ebook.title}</Text>
              <Text fontSize="xs" color="gray.400">{bulkBisacModal.ebook.isbn}</Text>
            </Box>
            {BISAC_FIELDS.map(({ id, field }) => {
              const f = bulkBisacModal.fields[field];
              return (
                <Box key={field}>
                  <Text fontWeight="600" fontSize="xs" color="gray.500" mb={1}>{t(id)}</Text>
                  <HStack spacing={2}>
                    <Select
                      size="sm"
                      flex={2}
                      placeholder=" "
                      value={f.heading}
                      onChange={(e) => handleBulkHeadingChange(field, e.target.value)}
                      isDisabled={bulkBisacModal.loadingHeadings}
                    >
                      {f.heading && !bulkBisacModal.headings.includes(f.heading) && (
                        <option value={f.heading}>{f.heading}</option>
                      )}
                      {bulkBisacModal.headings.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </Select>
                    <Select
                      size="sm"
                      flex={2}
                      placeholder=" "
                      value={f.subHeading}
                      onChange={(e) => handleBulkSubHeadingChange(field, e.target.value)}
                      isDisabled={!f.heading}
                    >
                      {f.subHeading && !f.subHeadings.includes(f.subHeading) && (
                        <option value={f.subHeading}>{f.subHeading}</option>
                      )}
                      {f.subHeadings.map((sh) => (
                        <option key={sh} value={sh}>{sh}</option>
                      ))}
                    </Select>
                    <Input
                      size="sm"
                      flex={1}
                      value={f.code}
                      onChange={(e) => handleBulkCodeChange(field, e.target.value)}
                      maxLength={9}
                    />
                  </HStack>
                </Box>
              );
            })}
          </VStack>
        )}
      </ConfirmationModal>
    </>
  );
};

export default EBooksPage;
